import gql from 'graphql-tag'

const CloneApplicationBase = gql`
  mutation cloneApplicationBase(
    $id: ID!
    $brand: StoreBrand!
    $suppressedBrand: StoreBrand
    $suppressedChannel: ApplicationChannel
  ) {
    cloneApplicationBase(
      id: $id
      brand: $brand
      suppressedBrand: $suppressedBrand
      suppressedChannel: $suppressedChannel
    ) {
      id
      source
      applicationType
      stateCode
      campaignId
      ipAddress
      status
      channel
      preferredProductCode
      suppressedBrands
      userInformation {
        email
      }
      customerInformation {
        personalInformation {
          firstName
          lastName
          dateOfBirth
          documentType
          documentNumber
          stateIdType
          driverLicenseNumber
          driverLicenseStateCode
          stateIdExpirationDate
        }
        contactInformation {
          address {
            addressLine1
            addressLine2
            cityName
            stateCode
            zipCode
          }
          phones {
            type
            number
            extension
            phonePreference
          }
          emails {
            type
            email
            verified
          }
        }
        additionalInformation {
          monthsAtAddress
          ownResidence
        }
        spousalInformation {
          firstName
          lastName
          email
          phoneNumber
        }
        bankingInformation {
          isReadOnly
          routingNumber
          accountNumber
          accountHoldersName
          bankName
          accountBalance
          accountOpenDate
        }
        incomeInformation {
          type
          employmentInformation {
            name
          }
          employmentHireDate
          incomeWithholding
          jobTitle
          paymentFrequency
          paymentMethod
          amountGross
          amountNet
          monthlyPaymentType
          weekOfMonth
          nextPaymentDate
          paymentDayOfWeek
          jobShift
        }
      }
      storeInformation {
        storeNumber
        brandName
      }
      loanApplication {
        additionalAttributes {
          optOut
          canShareInfo
          smsOptIn
          otpResponse
          iovationBlackBox
          flagAutoOrigination
          disclosures {
            idTag
            value
          }
        }
      }
      loanApplicationProgress {
        applicationUrl
      }
      createdAt
      modifiedAt
    }
  }
`

const ConvertApplicationTypeToConversionMutation = gql`
  mutation convertApplicationTypeToConversion(
    $id: ID!
    $loanApplicationDetails: LoanApplicationDetailsInput
  ) {
    convertApplicationTypeToConversion(
      id: $id
      loanApplicationDetails: $loanApplicationDetails
    ) {
      id
      applicationType
      channel
      preferredProductCode
      userInformation {
        email
      }
      storeInformation {
        storeNumber
        brandName
      }
      loanApplicationProgress {
        applicationUrl
      }
      initialApplicationDetails {
        stateCode
        product {
          productCode
          channel
        }
      }
      conversionDetails {
        loanNumber
        productCode
        payoffAmount
      }
      status
    }
  }
`

const createApplicationMutation = gql`
  mutation createApplication(
    $application: ApplicationCreateInput!
    $cmsData: String
  ) {
    create(application: $application, cmsData: $cmsData) {
      id
      applicationType
      channel
      stateCode
      status
      customerInformation {
        personalInformation {
          firstName
          lastName
        }
      }
      userInformation {
        email
      }
      loanApplicationProgress {
        applicationUrl
      }
      storeInformation {
        storeNumber
        brandName
      }
    }
  }
`

const CreateConversionDenialMutation = gql`
  mutation createConversionDenial($id: ID!) {
    createConversionDenial(id: $id) {
      id
    }
  }
`

const CreateReturningCustomerApplicationMutation = gql`
  mutation createReturningApplication(
    $applicationType: ApplicationType
    $brandName: String
    $isGPCEnabled: Boolean
    $loanApplicationDetails: LoanApplicationDetailsInput
  ) {
    createReturningApplication(
      applicationType: $applicationType
      brandName: $brandName
      isGPCEnabled: $isGPCEnabled
      loanApplicationDetails: $loanApplicationDetails
    ) {
      id
      applicationType
      channel
      status
      stateCode
      customerInformation {
        personalInformation {
          firstName
          lastName
          documentType
          documentNumber
          dateOfBirth
          stateIdType
          driverLicenseStateCode
          driverLicenseNumber
        }
        contactInformation {
          phones {
            type
            number
            extension
            phonePreference
          }
          emails {
            type
            email
            verified
          }
          address {
            addressLine1
            addressLine2
            cityName
            zipCode
            stateCode
          }
        }
        additionalInformation {
          monthsAtAddress
          ownResidence
        }
      }
      userInformation {
        email
      }
      loanApplicationProgress {
        applicationUrl
      }
      storeInformation {
        storeNumber
        brandName
      }
    }
  }
`

const PrepareForEnrollmentMutation = gql`
  mutation prepareForEnrollment($applicationId: ID!, $ssn: String) {
    prepareForEnrollment(applicationId: $applicationId, ssn: $ssn)
  }
`

const SaveAgreementsMutation = gql`
  mutation saveAgreements(
    $id: ID!
    $agreements: AgreementsInput!
    $repaymentMethod: String
    $fundingMethod: String
    $pdlOutstandingTotalDue: String
    $pdlOutstandingPrincipalDue: String
  ) {
    saveAgreements(
      id: $id
      agreements: $agreements
      repaymentMethod: $repaymentMethod
      fundingMethod: $fundingMethod
      pdlOutstandingTotalDue: $pdlOutstandingTotalDue
      pdlOutstandingPrincipalDue: $pdlOutstandingPrincipalDue
    ) {
      id
      customerInformation {
        spousalInformation {
          firstName
          lastName
          email
          phoneNumber
        }
      }
      loanApplication {
        additionalAttributes {
          optOut
          canShareInfo
          smsOptIn
          otpResponse
          iovationBlackBox
          flagAutoOrigination
          disclosures {
            idTag
            value
          }
          marketingConsentSms
          marketingConsentPhoneCall
        }
      }
      loanApplicationProgress {
        applicationUrl
      }
      applicationState
    }
  }
`

const SaveBankInformationMutation = gql`
  mutation saveBankInformation($id: ID!, $bankInfo: BankInformationInput!) {
    saveBankInformation(id: $id, bankInformation: $bankInfo) {
      id
      customerInformation {
        bankingInformation {
          isReadOnly
          routingNumber
          accountNumber
          accountHoldersName
          bankName
          accountOpenDate
          accountBalance
        }
      }
      loanApplicationProgress {
        applicationUrl
      }
    }
  }
`

const SaveBankVerificationDecision = gql`
  mutation saveDecision($id: ID!, $decision: BankVerificationDecisionInput!) {
    saveDecisionBankVerification(id: $id, decision: $decision) {
      requestCode
      siteResult
      decisionResult
    }
  }
`

const SaveIncomeInformationMutation = gql`
  mutation saveIncomeInformation(
    $id: ID!
    $incomeInfo: [FormIncomeInformationInput!]!
    $agreements: IncomeAgreementInput
  ) {
    saveIncomeInformation(
      id: $id
      incomeInformation: $incomeInfo
      agreements: $agreements
    ) {
      id
      customerInformation {
        contactInformation {
          phones {
            type
            number
            extension
            phonePreference
          }
        }
        incomeInformation {
          type
          employmentInformation {
            name
            workPhone
            workExtension
          }
          employmentHireDate
          incomeWithholding
          jobTitle
          paymentFrequency
          paymentMethod
          amountGross
          amountNet
          monthlyPaymentType
          weekOfMonth
          nextPaymentDate
          paymentDayOfWeek
          jobShift
          isPrimaryIncome
        }
      }
      loanApplication {
        additionalAttributes {
          optOut
          canShareInfo
          smsOptIn
          otpResponse
          iovationBlackBox
          flagAutoOrigination
          disclosures {
            idTag
            value
          }
          marketingConsentSms
          marketingConsentPhoneCall
        }
      }
      loanApplicationProgress {
        applicationUrl
      }
      applicationState
      status
      invalidPrecheck
    }
  }
`
const SaveLoanApplicationProgressMutation = gql`
  mutation saveLoanApplicationProgress($id: ID!, $applicationUrl: String!) {
    saveLoanApplicationProgress(id: $id, applicationUrl: $applicationUrl) {
      id
      loanApplicationProgress {
        applicationUrl
      }
    }
  }
`
const SavePersonalInformationMutation = gql`
  mutation savePersonalInformation(
    $id: ID!
    $personalInfo: FormPersonalInformationInput!
  ) {
    savePersonalInformation(id: $id, personalInformation: $personalInfo) {
      id
      applicationNumber
      stateCode
      channel
      preferredProductCode
      customerInformation {
        personalInformation {
          firstName
          lastName
          documentType
          documentNumber
          dateOfBirth
          stateIdType
          driverLicenseStateCode
          driverLicenseNumber
          stateIdExpirationDate
        }
        contactInformation {
          phones {
            type
            number
            extension
            phonePreference
          }
          emails {
            type
            email
            verified
          }
          address {
            addressLine1
            addressLine2
            cityName
            zipCode
            stateCode
          }
        }
        additionalInformation {
          monthsAtAddress
          ownResidence
        }
      }
      loanApplicationProgress {
        applicationUrl
      }
      loanApplication {
        additionalAttributes {
          optOut
          canShareInfo
          smsOptIn
          otpResponse
          iovationBlackBox
          flagAutoOrigination
          disclosures {
            idTag
            value
          }
          marketingConsentSms
          marketingConsentPhoneCall
          marketingConsent
        }
      }
      invalidPrecheck
    }
  }
`

const SaveStoreInformationMutation = gql`
  mutation saveStoreInformation(
    $id: ID!
    $storeInfo: StoreInformationInput!
    $loanProgress: LoanApplicationProgressInput!
  ) {
    saveStoreInformation(
      id: $id
      storeInformation: $storeInfo
      loanApplicationProgress: $loanProgress
    ) {
      id
      storeInformation {
        storeNumber
        brandName
      }
      loanApplicationProgress {
        applicationUrl
      }
    }
  }
`

const SaveUserInformation = gql`
  mutation saveUserInformation(
    $id: ID!
    $userInformation: UserInformationInput!
  ) {
    saveUserInformation(id: $id, userInformation: $userInformation) {
      id
    }
  }
`

const SubmitApplicationMutation = gql`
  mutation submitApplication($id: ID!) {
    submitApplication(id: $id) {
      id
      applicationState
    }
  }
`

const WithdrawOpenApplicationMutation = gql`
  mutation withdrawApplication($id: ID!) {
    withdrawApplication(id: $id) {
      id
      userInformation {
        email
      }
    }
  }
`

const CloneApprovedLeadApplicationMutation = gql`
  mutation cloneApprovedLeadApplication($id: ID!) {
    cloneApprovedLeadApplication(id: $id) {
      id
      channel
    }
  }
`

const SaveChannelPreferenceMutation = gql`
  mutation saveChannelPreference($id: ID!, $request: ProductPreferenceInput!) {
    saveApplicationChannelPreference(id: $id, request: $request) {
      id
      channel
      applicationState
    }
  }
`

const HandlePlaidEventsMutation = gql`
  mutation handlePlaidEvents($applicationId: String!, $eventResult: String) {
    handlePlaidEvents(
      applicationId: $applicationId
      eventResult: $eventResult
    ) {
      id
      applicationId
      status
      attempts
    }
  }
`

const UpsertApplicationMetricsMutation = gql`
  mutation upsertApplicationMetrics($id: ID!, $applicationMetric: ApplicationMetricInput!) {
    upsertApplicationMetrics(id: $id, applicationMetric: $applicationMetric) {
      id
      applicationMetrics {
        name
        origin
        source
        description
        value
        context {
          customerType
        }
      }
    }
  }
`

export const MatchLeadsWithLender = gql`
  mutation matchLeadsWithLender($applicationId: ID!, $leadSaleId: ID!) {
    matchLeadsWithLender(applicationId: $applicationId, leadSaleId: $leadSaleId) {
      redirectUrl
      accepted
    }
  }
`

export const CheckLeadSellingEligibility = gql`
  mutation checkApplicationLeadSellingEligibility($applicationId: ID!) {
    checkApplicationLeadSellingEligibility(applicationId: $applicationId) {
      isEligible
      id
    }
  }
`

export default {
  createApplicationMutation,
  CreateConversionDenialMutation,
  CreateReturningCustomerApplicationMutation,
  PrepareForEnrollmentMutation,
  SaveUserInformation,
  SavePersonalInformationMutation,
  SaveIncomeInformationMutation,
  SaveBankInformationMutation,
  SaveAgreementsMutation,
  SaveStoreInformationMutation,
  SaveBankVerificationDecision,
  CloneApplicationBase,
  WithdrawOpenApplicationMutation,
  CloneApprovedLeadApplicationMutation,
  HandlePlaidEventsMutation,
  SaveChannelPreferenceMutation,
  UpsertApplicationMetricsMutation,
  MatchLeadsWithLender,
  CheckLeadSellingEligibility
}
export {
  CloneApplicationBase,
  ConvertApplicationTypeToConversionMutation,
  createApplicationMutation,
  CreateConversionDenialMutation,
  CreateReturningCustomerApplicationMutation,
  PrepareForEnrollmentMutation,
  SaveAgreementsMutation,
  SaveBankInformationMutation,
  SaveBankVerificationDecision,
  SaveIncomeInformationMutation,
  SavePersonalInformationMutation,
  SaveStoreInformationMutation,
  SaveLoanApplicationProgressMutation,
  SaveUserInformation,
  SubmitApplicationMutation,
  WithdrawOpenApplicationMutation,
  CloneApprovedLeadApplicationMutation,
  HandlePlaidEventsMutation,
  SaveChannelPreferenceMutation,
  UpsertApplicationMetricsMutation,
}
